import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { RootState } from "..";
import { isTV } from "../../utils/platform";

export type CrossInputMode = "mouse" | "gamepad" | "remote";
export type ScreenSize =
  | "mobile"
  | "tablet"
  | "desktop"
  | "tablet-landscape"
  | "mobile-landscape";

export interface ClientClusterMetric {
  name: string;
  reachable: boolean;
  metrics: { ping: number }[];
}

interface IAppState {
  prevSearchQuery: string;
  hasConnectedMouseKeyboard: boolean;
  inputMode?: CrossInputMode;
  screenSize?: ScreenSize;
  clientClusterMetrics?: ClientClusterMetric[];
  isSubscriptionRequiredOpen: boolean;
}

const initialState: IAppState = {
  prevSearchQuery: "",
  hasConnectedMouseKeyboard: false,
  inputMode: undefined,
  clientClusterMetrics: undefined,
  isSubscriptionRequiredOpen: false,
};

export const appStateSlice = createSlice({
  name: "appState",
  initialState,
  reducers: {
    setPrevSearchQuery: (state, action: PayloadAction<string>) => {
      state.prevSearchQuery = action.payload;
    },
    setHasConnectedMouseKeyboard: (state, action: PayloadAction<boolean>) => {
      state.hasConnectedMouseKeyboard = action.payload;
    },
    setInputMode: (state, action: PayloadAction<CrossInputMode>) => {
      state.inputMode = action.payload;
    },
    setScreenSize: (state, action: PayloadAction<ScreenSize>) => {
      state.screenSize = action.payload;
    },
    setClientClusterMetrics: (
      state,
      action: PayloadAction<ClientClusterMetric[]>,
    ) => {
      state.clientClusterMetrics = action.payload;
    },
    setIsSubscriptionRequiredOpen: (state, action: PayloadAction<boolean>) => {
      state.isSubscriptionRequiredOpen = action.payload;
    },
  },
});

export const {
  setPrevSearchQuery,
  setHasConnectedMouseKeyboard,
  setInputMode,
  setScreenSize,
  setClientClusterMetrics,
  setIsSubscriptionRequiredOpen,
} = appStateSlice.actions;

export const selectPrevSearchQuery = (state: RootState) =>
  state.appState.prevSearchQuery;
export const selectHasConnectedMouseKeyboard = (state: RootState) =>
  state.appState.hasConnectedMouseKeyboard;
export const selectIsDesktop = (state: RootState) =>
  state.appState.screenSize === "desktop";
export const selectIsMobile = (state: RootState) =>
  state.appState.screenSize === "mobile" ||
  state.appState.screenSize === "mobile-landscape";
export const selectIsMobileOrTablet = (state: RootState) =>
  state.appState.screenSize === "mobile" ||
  state.appState.screenSize === "mobile-landscape" ||
  state.appState.screenSize === "tablet" ||
  state.appState.screenSize === "tablet-landscape";
export const selectIsDesktopView = (state: RootState) =>
  state.appState.screenSize === "desktop" ||
  state.appState.screenSize === "tablet-landscape";
export const selectIsSubscriptionRequiredOpen = (state: RootState) =>
  state.appState.isSubscriptionRequiredOpen;

export default appStateSlice.reducer;
